import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AiSnapshot, AiLibraryFolder, AiLibrarySnapshot, AiSharedLibrarySnapshot, AiLibraryShare, PssActionType, PssAmmoDesign, PssConditionType, PssAuthService } from '../../../../services/pss-auth.service';
import { AiImportPayload, AiImportRule, AiRuleLike } from '../ai-editor.types';
import { PssSpriteComponent } from '../../../pss-sprite/pss-sprite.component';

@Component({
  selector: 'app-ai-pane',
  standalone: true,
  imports: [CommonModule, FormsModule, PssSpriteComponent],
  templateUrl: './ai-pane.component.html',
  styleUrls: ['./ai-pane.component.css']
})
export class AiPaneComponent implements OnChanges {
  @Input() rules: AiRuleLike[] = [];
  @Input() conditionTypes: PssConditionType[] = [];
  @Input() actionTypes: PssActionType[] = [];
  /**
   * The room's pool in "Set Item {N}" order: ammo for weapons, craft for
   * hangars, droids for android studios. Index N is what that action loads.
   * Empty for crew AI, where the pool depends on whichever room the crew is
   * standing in and so cannot be resolved to a single name.
   */
  @Input() itemsByIndex: PssAmmoDesign[] = [];

  @Input() saving = false;
  @Input() error = '';
  @Input() emptyMessage = 'No AI rules set.';

  @Input() targetType: 'crew' | 'room' = 'crew';
  @Input() targetId = '';
  @Input() targetName = '';
  @Input() targetSpriteId = '';

  /** Current user's PSS userId — needed for library and sharing. */
  @Input() userId = '';
  /** Current user's alliance/fleet ID — used for fleet-share scoping. */
  @Input() allianceId = '';
  /** Current user's display name — used for feature gating. */
  @Input() userName = '';

  /** When true the pane is in the expanded full-screen editor — adjusts font sizes. */
  @Input() expanded = false;

  @Input() initialConditionId = '';
  @Input() initialActionId = '';

  @Output() addRule    = new EventEmitter<{ conditionTypeId: string; actionTypeId: string }>();
  @Output() deleteRule = new EventEmitter<AiRuleLike>();
  @Output() moveRule   = new EventEmitter<{ rule: AiRuleLike; newIndex: number }>();
  @Output() importRules  = new EventEmitter<AiImportPayload>();
  @Output() deleteAllRules = new EventEmitter<void>();
  @Output() editRule = new EventEmitter<{ rule: AiRuleLike; conditionTypeId: string; actionTypeId: string }>();
  /**
   * Emitted after every local mutation with the full current rule array.
   * Parent can use this to keep their copy in sync without needing to
   * re-pass the `rules` input.
   */
  @Output() rulesChanged = new EventEmitter<AiRuleLike[]>();

  // ── Internal mutable copy — optimistic UI ────────────────────────────────
  internalRules: AiRuleLike[] = [];

  // ── Drag state ────────────────────────────────────────────────────────────
  dragSourceIndex: number | null = null;
  dropIndicatorIndex: number | null = null;

  // ── Inline edit state ─────────────────────────────────────────────────────
  /** Index into sortedRules of the row being edited (null = no active edit). */
  editingIndex: number | null = null;
  editConditionId = '';
  editConditionSearch = '';
  editActionId = '';
  editActionSearch = '';
  editConditionOpen = false;
  editActionOpen = false;

  // ── Combo/search state ────────────────────────────────────────────────────
  conditionSearch = '';
  conditionOpen = false;
  actionSearch = '';
  actionOpen = false;
  selectedConditionId = '';
  selectedActionId = '';

  // ── Copy / import state ───────────────────────────────────────────────────
  copied = false;
  showImport = false;
  importText = '';
  importError = '';
  pendingImport: AiImportRule[] | null = null;
  appendToEnd = false;

  // ── Line selection mode for partial copy ───────────────────────────────────
  lineSelectionMode = false;
  selectedRuleKeys = new Set<string>();
  showDeleteSelectedConfirm = false;

  // ── Snapshot selection state ───────────────────────────────────────────────
  snapshotSelectedRules: AiRuleLike[] | null = null;

  // ── Snapshot state ────────────────────────────────────────────────────────
  showSnapshots = false;
  snapshotsLoading = false;
  snapshots: AiSnapshot[] = [];
  showSaveSnapshot = false;
  showSaveSnapshotConfirm = false;
  dontShowSaveSnapshotWarning = false;
  newSnapshotName = '';
  savingSnapshot = false;
  snapshotError = '';

  // ── Library state ─────────────────────────────────────────────────────────
  showLibrary = false;
  libraryPreviewEntry: AiLibrarySnapshot | AiSharedLibrarySnapshot | null = null;
  libraryLoading = false;
  libraryFolders: AiLibraryFolder[] = [];
  libraryUnfiled: AiLibrarySnapshot[] = [];
  libraryShared: AiSharedLibrarySnapshot[] = [];
  libraryError = '';

  // Save new entry
  showSaveLibrary = false;
  newLibraryName = '';
  newLibraryDescription = '';
  newLibraryFolderId: string | null = null;
  savingLibrary = false;

  // New folder
  showNewFolder = false;
  newFolderName = '';
  savingFolder = false;

  // Snapshot → add to library
  snapshotAddToLibrary = false;
  snapshotSaveToFolderId: string | null = null;

  // Library nav — section collapse (default expanded) + selected view
  yourLibrarySectionExpanded = true;
  sharedSectionExpanded = true;
  selectedLibraryView: { type: 'folder' | 'unfiled' | 'shared'; folderId?: string } | null = null;

  // Library search
  librarySearch = '';

  // Share panel — entry (unfiled only)
  sharingLibraryId: string | null = null;
  shareType: 'fleet' | 'player' = 'fleet';
  // PSS player search for entry share
  sharePlayerQuery = '';
  sharePlayerResults: { userId: string; userName: string; allianceId: string }[] = [];
  sharePlayerSearching = false;
  sharePlayerSelected: { userId: string; userName: string } | null = null;
  sharingInProgress = false;
  shareError = '';

  // Share panel — folder
  sharingFolderId: string | null = null;
  folderShareType: 'fleet' | 'player' = 'fleet';
  // PSS player search for folder share
  folderSharePlayerQuery = '';
  folderSharePlayerResults: { userId: string; userName: string; allianceId: string }[] = [];
  folderSharePlayerSearching = false;
  folderSharePlayerSelected: { userId: string; userName: string } | null = null;
  folderSharingInProgress = false;
  folderShareError = '';

  // Move entry to folder
  movingEntryId: string | null = null;

  // Inline new-folder modal (triggered from folder select)
  showInlineFolderModal = false;
  inlineFolderName = '';
  savingInlineFolder = false;
  inlineFolderError = '';

  static readonly MAX_RULES = 55;

  constructor(private auth: PssAuthService) {
    this.loadSnapshotSaveWarningSetting();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rules']) {
      // Always sync from the authoritative parent input.
      // The stable trackByRule (using characterActionId) preserves DOM elements across
      // rebuilds, so Angular won't destroy/recreate the dragged element mid-drag.
      this.internalRules = this.normalize(this.rules);
    }

    if (changes['initialConditionId']) {
      this.applyConditionSelection(this.initialConditionId);
    }
    if (changes['initialActionId']) {
      this.applyActionSelection(this.initialActionId);
    }

    if (changes['conditionTypes']) {
      const sel = this.conditionTypes.find(c => c.id === this.selectedConditionId);
      if (this.selectedConditionId && !sel) {
        this.selectedConditionId = '';
        this.conditionSearch = '';
      } else if (sel && this.matchesValue(this.conditionSearch, sel.name)) {
        this.conditionSearch = sel.name;
      }
    }

    if (changes['actionTypes']) {
      const sel = this.actionTypes.find(a => a.id === this.selectedActionId);
      if (this.selectedActionId && !sel) {
        this.selectedActionId = '';
        this.actionSearch = '';
      } else if (sel && this.matchesValue(this.actionSearch, sel.name)) {
        this.actionSearch = sel.name;
      }
    }

    if (changes['targetId']) {
      this.snapshots = [];
      this.showSnapshots = false;
      this.showSaveSnapshot = false;
      this.snapshotError = '';
      this.editingIndex = null;
    }
  }

  // ── Internal helpers ──────────────────────────────────────────────────────

  /**
   * Returns a stable trackBy key for a rule using its DB action ID.
   * Using a stable ID means Angular reuses the DOM element across re-renders,
   * which prevents the browser from cancelling an in-progress drag.
   */
  private stableKey(rule: AiRuleLike): string {
    const id = (rule as any).characterActionId ?? (rule as any).roomActionId ?? null;
    return id ? String(id) : `${rule.index}-${rule.conditionTypeId}-${rule.actionTypeId}`;
  }

  private normalize(rules: AiRuleLike[]): AiRuleLike[] {
    return [...rules].sort((a, b) => a.index - b.index).map((r, i) => ({ ...r, index: i }));
  }

  private mutate(newRules: AiRuleLike[]): void {
    this.internalRules = this.normalize(newRules);
    this.rulesChanged.emit([...this.internalRules]);
  }

  // ── Sorted / visual views ─────────────────────────────────────────────────

  get sortedRules(): AiRuleLike[] {
    return [...this.internalRules].sort((a, b) => a.index - b.index);
  }

  trackByRule = (_idx: number, rule: AiRuleLike): string => this.stableKey(rule);

  // ── Drag and drop ─────────────────────────────────────────────────────────

  onDragStart(event: DragEvent, index: number): void {
    if (this.saving || this.editingIndex !== null) { event.preventDefault(); return; }
    this.dragSourceIndex = index;
    event.dataTransfer!.effectAllowed = 'move';
  }

  onDragOver(event: DragEvent, index: number): void {
    if (this.dragSourceIndex === null) return;
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.dropIndicatorIndex = event.clientY < rect.top + rect.height / 2 ? index : index + 1;
  }

  onListDragOver(event: DragEvent): void {
    if (this.dragSourceIndex === null) return;
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.performDrop();
  }

  onListDrop(event: DragEvent): void {
    event.preventDefault();
    this.performDrop();
  }

  onDragEnd(): void {
    this.clearDrag();
  }

  private performDrop(): void {
    const src = this.dragSourceIndex;
    const ind = this.dropIndicatorIndex;
    this.clearDrag();
    if (src === null || ind === null) return;
    if (ind === src || ind === src + 1) return; // no-op: would land in same position
    const dest = ind <= src ? ind : ind - 1;
    const sorted = [...this.sortedRules];
    const movedRule = sorted[src];
    const [moved] = sorted.splice(src, 1);
    sorted.splice(dest, 0, moved);
    this.mutate(sorted);
    this.moveRule.emit({ rule: movedRule, newIndex: dest });
  }

  private clearDrag(): void {
    this.dragSourceIndex = null;
    this.dropIndicatorIndex = null;
  }

  // ── Inline row editing ────────────────────────────────────────────────────

  onSelectRule(rule: AiRuleLike, index: number): void {
    if (this.saving || this.dragSourceIndex !== null) return;
    this.editingIndex = index;
    this.editConditionId = rule.conditionTypeId;
    this.editConditionSearch = this.conditionName(rule.conditionTypeId);
    this.editActionId = rule.actionTypeId;
    this.editActionSearch = this.actionName(rule.actionTypeId);
    this.editConditionOpen = false;
    this.editActionOpen = false;
  }

  onRuleContainerClick(rule: AiRuleLike, index: number, event: MouseEvent): void {
    if (this.lineSelectionMode || this.saving || this.editingIndex === index) return;

    // Safety: click target may be text node (✓), so resolve closest element in composed path.
    const path = event.composedPath ? event.composedPath() : [event.target];
    const clickedEl = path.find((node): node is HTMLElement => node instanceof HTMLElement);
    if (!clickedEl) return;

    const ignoreSelector = '.ai-delete-btn, .ai-drag-handle, .ai-save-edit-btn, .ai-cancel-edit-btn, .ai-combo-input, .ai-combo-drop, .ai-combo-opt';
    if (clickedEl.closest(ignoreSelector)) return;

    this.onSelectRule(rule, index);
  }

  onSaveEdit(event: MouseEvent): void {
    event.stopPropagation();
    if (this.editingIndex === null || this.saving || !this.editConditionId || !this.editActionId) return;
    const rule = this.sortedRules[this.editingIndex];
    if (!rule) { this.editingIndex = null; return; }
    if (rule.conditionTypeId === this.editConditionId && rule.actionTypeId === this.editActionId) {
      this.editingIndex = null;
      return;
    }
    // Optimistically update local display state
    const idx = this.internalRules.indexOf(rule);
    if (idx !== -1) {
      this.internalRules[idx] = { ...rule, conditionTypeId: this.editConditionId, actionTypeId: this.editActionId };
      this.internalRules = this.normalize(this.internalRules);
      this.rulesChanged.emit([...this.internalRules]);
    }
    // Always emit the original rule so the parent can locate it by characterActionId / roomActionId
    this.editRule.emit({ rule, conditionTypeId: this.editConditionId, actionTypeId: this.editActionId });
    this.editingIndex = null;
  }

  onCancelEdit(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.editingIndex = null;
  }

  openEditConditionCombo(): void { this.editConditionOpen = true; }
  openEditActionCombo(): void    { this.editActionOpen = true; }

  onEditConditionInput(): void { this.editConditionOpen = true; this.syncEditConditionFromSearch(); }
  onEditActionInput(): void    { this.editActionOpen = true;    this.syncEditActionFromSearch(); }

  selectEditCondition(c: PssConditionType): void {
    this.editConditionId = c.id;
    this.editConditionSearch = c.name;
    this.editConditionOpen = false;
  }

  selectEditAction(a: PssActionType): void {
    this.editActionId = a.id;
    this.editActionSearch = a.name;
    this.editActionOpen = false;
  }

  closeEditCombo(which: 'cond' | 'act'): void {
    setTimeout(() => {
      if (which === 'cond') this.editConditionOpen = false;
      if (which === 'act')  this.editActionOpen = false;
    }, 150);
  }

  filteredEditConditionTypes(): PssConditionType[] {
    if (!this.editConditionSearch.trim()) return this.conditionTypes;
    const sel = this.conditionTypes.find(c => c.id === this.editConditionId);
    if (sel && sel.name.toLowerCase() === this.editConditionSearch.trim().toLowerCase()) return this.conditionTypes;
    return this.conditionTypes.filter(c => c.name.toLowerCase().includes(this.editConditionSearch.trim().toLowerCase()));
  }

  filteredEditActionTypes(): PssActionType[] {
    if (!this.editActionSearch.trim()) return this.actionTypes;
    const sel = this.actionTypes.find(a => a.id === this.editActionId);
    if (sel && sel.name.toLowerCase() === this.editActionSearch.trim().toLowerCase()) return this.actionTypes;
    return this.actionTypes.filter(a => this.actionMatches(a, this.editActionSearch.trim().toLowerCase()));
  }

  private syncEditConditionFromSearch(): void {
    const match = this.conditionTypes.find(c => this.matchesValue(this.editConditionSearch, c.name));
    this.editConditionId = match?.id ?? '';
  }

  private syncEditActionFromSearch(): void {
    const match = this.actionTypes.find(a => this.matchesValue(this.editActionSearch, a.name));
    this.editActionId = match?.id ?? '';
  }

  // ── Add / Delete / Delete All ─────────────────────────────────────────────

  showDeleteAllConfirm = false;

  onAddRule(): void {
    if (this.saving || !this.selectedConditionId || !this.selectedActionId) return;
    this.mutate([
      ...this.internalRules,
      { index: this.internalRules.length, conditionTypeId: this.selectedConditionId, actionTypeId: this.selectedActionId }
    ]);
    this.addRule.emit({ conditionTypeId: this.selectedConditionId, actionTypeId: this.selectedActionId });
    this.applyConditionSelection('');
    this.applyActionSelection('');
    this.conditionOpen = false;
    this.actionOpen = false;
  }

  onDeleteRule(rule: AiRuleLike, event: MouseEvent): void {
    event.stopPropagation();
    this.mutate(this.internalRules.filter(r => r !== rule));
    this.deleteRule.emit(rule);
  }

  onDeleteAllRules(): void {
    this.showDeleteAllConfirm = true;
  }

  confirmDeleteAllRules(): void {
    this.showDeleteAllConfirm = false;
    this.mutate([]);
    this.deleteAllRules.emit();
  }

  cancelDeleteAllRules(): void {
    this.showDeleteAllConfirm = false;
  }

  // ── Condition / Action combos ─────────────────────────────────────────────

  filteredConditionTypes(): PssConditionType[] {
    const s = this.shouldShowAllConditionOptions() ? '' : this.conditionSearch.trim().toLowerCase();
    return this.conditionTypes.filter(c => c.name.toLowerCase().includes(s));
  }

  filteredActionTypes(): PssActionType[] {
    const s = this.shouldShowAllActionOptions() ? '' : this.actionSearch.trim().toLowerCase();
    return this.actionTypes.filter(a => this.actionMatches(a, s));
  }

  openConditionCombo(): void { this.conditionOpen = true; }
  openActionCombo(): void    { this.actionOpen = true; }

  onConditionInput(): void { this.conditionOpen = true; this.syncConditionSelectionFromSearch(); }
  onActionInput(): void    { this.actionOpen = true;    this.syncActionSelectionFromSearch(); }

  selectCondition(c: PssConditionType): void {
    this.applyConditionSelection(c.id);
    this.conditionOpen = false;
  }

  selectAction(a: PssActionType): void {
    this.applyActionSelection(a.id);
    this.actionOpen = false;
  }

  closeCombo(which: 'cond' | 'act'): void {
    setTimeout(() => {
      if (which === 'cond') this.conditionOpen = false;
      if (which === 'act')  this.actionOpen = false;
    }, 150);
  }

  conditionName(id: string): string { return this.conditionTypes.find(c => c.id === id)?.name ?? `Cond#${id}`; }
  actionName(id: string): string    { return this.actionTypes.find(a => a.id === id)?.name ?? `Act#${id}`; }

  /**
   * Display label for an action. Reads the same as `actionName` except for the
   * indexed ammo picks, where "Set Item {3}" becomes "Set Item: Penetrator".
   * Kept separate from `actionName` because the combo boxes match their own
   * search text against the raw PSS name.
   */
  actionLabel(id: string): string {
    const action = this.actionTypes.find(a => a.id === id);
    if (!action) return `Act#${id}`;
    return this.itemNameFor(action) ? `Set Item: ${this.itemNameFor(action)}` : action.name;
  }

  /**
   * Label for an action inside a combo box. Keeps the PSS index visible next to
   * the item name, because selecting an option fills the input with the raw
   * name and a bare "Set Item: Scarlet" option would look like it changed.
   */
  actionOptionLabel(id: string): string {
    const action = this.actionTypes.find(a => a.id === id);
    if (!action) return `Act#${id}`;
    const item = this.itemNameFor(action);
    return item ? `${action.name} (${item})` : action.name;
  }

  /** Resolved item name for an indexed "Set Item {N}" action, else ''. */
  private itemNameFor(action: PssActionType): string {
    const m = /^SetItem(\d+)$/.exec(action.key ?? '');
    if (!m) return '';
    return this.itemsByIndex[parseInt(m[1], 10)]?.name ?? '';
  }

  /** True when `search` matches the action's PSS name or its resolved item name. */
  private actionMatches(action: PssActionType, search: string): boolean {
    if (action.name.toLowerCase().includes(search)) return true;
    const item = this.itemNameFor(action).toLowerCase();
    return !!item && item.includes(search);
  }

  private applyConditionSelection(id: string): void {
    const sel = id ? this.conditionTypes.find(c => c.id === id) ?? null : null;
    this.selectedConditionId = sel?.id ?? '';
    this.conditionSearch     = sel?.name ?? '';
  }

  private applyActionSelection(id: string): void {
    const sel = id ? this.actionTypes.find(a => a.id === id) ?? null : null;
    this.selectedActionId = sel?.id ?? '';
    this.actionSearch     = sel?.name ?? '';
  }

  private syncConditionSelectionFromSearch(): void {
    this.selectedConditionId = this.findExactConditionMatch(this.conditionSearch)?.id ?? '';
  }

  private syncActionSelectionFromSearch(): void {
    this.selectedActionId = this.findExactActionMatch(this.actionSearch)?.id ?? '';
  }

  private shouldShowAllConditionOptions(): boolean {
    if (!this.conditionOpen) return false;
    if (!this.conditionSearch.trim()) return true;
    const sel = this.conditionTypes.find(c => c.id === this.selectedConditionId);
    return !!sel && this.matchesValue(this.conditionSearch, sel.name);
  }

  private shouldShowAllActionOptions(): boolean {
    if (!this.actionOpen) return false;
    if (!this.actionSearch.trim()) return true;
    const sel = this.actionTypes.find(a => a.id === this.selectedActionId);
    return !!sel && this.matchesValue(this.actionSearch, sel.name);
  }

  private findExactConditionMatch(value: string): PssConditionType | null {
    const t = value.trim();
    return t ? this.conditionTypes.find(c => this.matchesValue(t, c.name)) ?? null : null;
  }

  private findExactActionMatch(value: string): PssActionType | null {
    const t = value.trim();
    return t ? this.actionTypes.find(a => this.matchesValue(t, a.name)) ?? null : null;
  }

  private matchesValue(left: string, right: string): boolean {
    return left.trim().toLowerCase() === right.trim().toLowerCase();
  }

  // ── Copy / Export ─────────────────────────────────────────────────────────

  get hasSelection(): boolean {
    return this.selectedRuleKeys.size > 0;
  }

  toggleLineSelectionMode(): void {
    this.lineSelectionMode = !this.lineSelectionMode;
    if (!this.lineSelectionMode) {
      this.selectedRuleKeys.clear();
      this.snapshotSelectedRules = null;
    }
  }

  isRuleSelected(rule: AiRuleLike): boolean {
    return this.selectedRuleKeys.has(this.stableKey(rule));
  }

  get selectedRules(): AiRuleLike[] {
    return this.sortedRules.filter(rule => this.selectedRuleKeys.has(this.stableKey(rule)));
  }

  deleteSelected(): void {
    if (!this.hasSelection) return;
    this.showDeleteSelectedConfirm = true;
  }

  confirmDeleteSelected(): void {
    const selected = this.selectedRules;
    if (!selected.length) {
      this.showDeleteSelectedConfirm = false;
      return;
    }

    this.showDeleteSelectedConfirm = false;
    this.mutate(this.internalRules.filter(rule => !this.selectedRuleKeys.has(this.stableKey(rule))));

    const emitSequentially = (index: number): void => {
      if (index >= selected.length) {
        this.selectedRuleKeys.clear();
        this.lineSelectionMode = false;
        return;
      }
      this.deleteRule.emit(selected[index]);
      setTimeout(() => emitSequentially(index + 1), 80);
    };

    emitSequentially(0);
  }

  cancelDeleteSelected(): void {
    this.showDeleteSelectedConfirm = false;
  }

  snapshotSelected(): void {
    if (!this.hasSelection) return;
    this.snapshotSelectedRules = this.selectedRules;
    this.showSnapshots = true;
    this.showSaveSnapshot = true;
  }

  onToggleRuleSelection(rule: AiRuleLike, event: Event): void {
    event.stopPropagation();
    const key = this.stableKey(rule);
    if (this.selectedRuleKeys.has(key)) {
      this.selectedRuleKeys.delete(key);
    } else {
      this.selectedRuleKeys.add(key);
    }
  }

  buildExportString(rules?: AiRuleLike[]): string {
    const set = rules ?? this.sortedRules;
    return set.map(r => `${r.conditionTypeId}x${r.actionTypeId}`).join('|');
  }

  copyExport(): void {
    const s = this.buildExportString();
    if (!s) return;
    navigator.clipboard.writeText(s).then(() => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    });
  }

  copySelectedExport(): void {
    if (!this.hasSelection) return;
    const s = this.buildExportString(this.selectedRules);
    if (!s) return;
    navigator.clipboard.writeText(s).then(() => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    });
  }

  // ── Import ────────────────────────────────────────────────────────────────

  onImport(): void {
    this.importError = '';
    this.pendingImport = null;
    const raw = this.importText.trim();
    if (!raw) { this.importError = 'Paste a Pixel Prestige URL or cmds string.'; return; }
    const tokens = raw.match(/\d+x\d+/g) ?? [];
    if (!tokens.length) { this.importError = 'No valid rules found. Expected format: 123x456|...'; return; }
    if (tokens.length > AiPaneComponent.MAX_RULES) {
      this.importError = `Too many rules (${tokens.length}). Maximum is ${AiPaneComponent.MAX_RULES}.`;
      return;
    }
    const parsed: AiImportRule[] = [];
    for (const token of tokens) {
      const sep = token.indexOf('x');
      const condId = token.slice(0, sep);
      const actId  = token.slice(sep + 1);
      if (!this.conditionTypes.find(c => c.id === condId)) { this.importError = `Unknown condition ID: ${condId}`; return; }
      if (!this.actionTypes.find(a => a.id === actId))     { this.importError = `Unknown action ID: ${actId}`;    return; }
      parsed.push({ conditionTypeId: condId, actionTypeId: actId });
    }
    this.pendingImport = parsed;
  }

  appendWouldExceedLimit(): boolean {
    if (!this.pendingImport || !this.appendToEnd) return false;
    return this.internalRules.length + this.pendingImport.length > AiPaneComponent.MAX_RULES;
  }

  showApplyOverwriteConfirm = false;

  onApplyClick(): void {
    if (!this.pendingImport || this.appendWouldExceedLimit()) return;
    if (!this.appendToEnd && this.internalRules.length > 0) {
      this.showApplyOverwriteConfirm = true;
      return;
    }
    this.onApplyImport();
  }

  confirmApplyOverwrite(): void {
    this.showApplyOverwriteConfirm = false;
    this.onApplyImport();
  }

  cancelApplyOverwrite(): void {
    this.showApplyOverwriteConfirm = false;
  }

  onApplyImport(): void {
    if (!this.pendingImport || this.appendWouldExceedLimit()) return;
    const base = this.appendToEnd ? this.sortedRules : [];
    const importList = this.pendingImport.map((r, i) => ({ index: base.length + i, ...r }));
    this.mutate([...base, ...importList]);
    this.importRules.emit({ rules: this.pendingImport, replaceAll: !this.appendToEnd });
    this.pendingImport = null;
    this.importText = '';
    this.showImport = false;
    this.appendToEnd = false;
  }

  onCancelImport(): void {
    this.pendingImport = null;
    this.importError = '';
    this.appendToEnd = false;
  }

  pendingConditionName(id: string): string { return this.conditionTypes.find(c => c.id === id)?.name ?? `Cond#${id}`; }
  pendingActionName(id: string): string    { return this.actionTypes.find(a => a.id === id)?.name ?? `Act#${id}`; }

  // ── Snapshots ─────────────────────────────────────────────────────────────

  toggleSnapshots(): void {
    this.showSnapshots = !this.showSnapshots;
    if (this.showSnapshots && !this.snapshotsLoading && this.snapshots.length === 0) {
      this.fetchSnapshots();
    }
  }

  private fetchSnapshots(): void {
    if (!this.targetId) return;
    this.snapshotsLoading = true;
    this.snapshotError = '';
    this.auth.getAiSnapshots(this.targetType, this.targetId).subscribe({
      next: snaps => { this.snapshots = snaps; this.snapshotsLoading = false; },
      error: ()   => { this.snapshotsLoading = false; },
    });
  }

  private static readonly SNAPSHOT_SAVE_WARNING_KEY = 'pss_ai_snapshot_save_warning_dont_show_again';

  onSaveSnapshot(): void {
    const name = this.newSnapshotName.trim();
    if (!name || this.savingSnapshot || !this.targetId) return;

    if (!this.dontShowSaveSnapshotWarning && !this.showSaveSnapshotConfirm) {
      this.showSaveSnapshotConfirm = true;
      return;
    }

    this.performSnapshotSave(name);
  }

  private performSnapshotSave(name: string): void {
    const sourceRules = this.snapshotSelectedRules && this.snapshotSelectedRules.length > 0
      ? this.snapshotSelectedRules
      : this.sortedRules;
    const rules = sourceRules.map(r => ({ conditionTypeId: r.conditionTypeId, actionTypeId: r.actionTypeId }));
    this.savingSnapshot = true;
    this.snapshotError = '';
    this.auth.saveAiSnapshot(this.targetType, this.targetId, name, rules).subscribe({
      next: snap => {
        this.snapshots.unshift(snap);
        this.newSnapshotName = '';
        this.showSaveSnapshot = false;
        this.showSaveSnapshotConfirm = false;
        this.savingSnapshot = false;
        this.snapshotSelectedRules = null;
        this.selectedRuleKeys.clear();
        this.lineSelectionMode = false;
        if (this.snapshotAddToLibrary) {
          this.auth.saveAiLibrary(name, '', rules, this.snapshotSaveToFolderId).subscribe({
            next: entry => {
              if (entry.folderId) {
                const folder = this.libraryFolders.find(f => f.folderId === entry.folderId);
                if (folder) folder.entries.push(entry);
              } else {
                this.libraryUnfiled.push(entry);
              }
            },
            error: () => { /* silent — snapshot was saved successfully */ },
          });
        }
        this.snapshotAddToLibrary = false;
        this.snapshotSaveToFolderId = null;
      },
      error: (e: any) => {
        this.snapshotError = e?.error?.error || 'Failed to save snapshot.';
        this.savingSnapshot = false;
        this.showSaveSnapshotConfirm = false;
      },
    });
  }

  confirmSaveSnapshot(): void {
    if (this.dontShowSaveSnapshotWarning) {
      this.persistSnapshotSaveWarningSetting();
    }
    this.showSaveSnapshotConfirm = false;
    this.performSnapshotSave(this.newSnapshotName.trim());
  }

  cancelSaveSnapshotConfirm(): void {
    this.showSaveSnapshotConfirm = false;
  }

  onSnapshotAddToLibraryChange(): void {
    if (this.snapshotAddToLibrary) {
      const empty = this.libraryFolders.length === 0 && this.libraryUnfiled.length === 0 && this.libraryShared.length === 0;
      if (!this.libraryLoading && empty) {
        this.fetchLibrary();
      }
    } else {
      this.snapshotSaveToFolderId = null;
    }
  }

  private loadSnapshotSaveWarningSetting(): void {
    try {
      this.dontShowSaveSnapshotWarning = localStorage.getItem(AiPaneComponent.SNAPSHOT_SAVE_WARNING_KEY) === 'true';
    } catch {
      this.dontShowSaveSnapshotWarning = false;
    }
  }

  private persistSnapshotSaveWarningSetting(): void {
    try {
      localStorage.setItem(AiPaneComponent.SNAPSHOT_SAVE_WARNING_KEY, String(true));
    } catch {
      // ignore storage errors
    }
  }

  onPreviewSnapshot(snap: AiSnapshot): void {
    const asRules: AiImportRule[] = snap.rules as AiImportRule[];
    this.pendingImport = asRules;
    this.importError = '';
    this.showImport = true;
    this.appendToEnd = false;
  }

  onDeleteSnapshot(snap: AiSnapshot): void {
    this.snapshotError = '';
    this.auth.deleteAiSnapshot(snap.snapshotId).subscribe({
      next: ()      => { this.snapshots = this.snapshots.filter(s => s.snapshotId !== snap.snapshotId); },
      error: (e: any) => { this.snapshotError = e?.error?.error || 'Failed to delete snapshot.'; },
    });
  }

  // ── Library ───────────────────────────────────────────────────────────────

  closeLibraryModal(): void {
    this.showLibrary = false;
    this.libraryPreviewEntry = null;
  }

  toggleLibrary(): void {
    this.showLibrary = true;
    const empty = this.libraryFolders.length === 0 && this.libraryUnfiled.length === 0 && this.libraryShared.length === 0;
    if (!this.libraryLoading && empty) {
      this.fetchLibrary();
    }
  }

  private fetchLibrary(): void {
    this.libraryLoading = true;
    this.libraryError = '';
    this.auth.getAiLibrary(this.allianceId).subscribe({
      next: r => {
        this.libraryFolders  = r.folders;
        this.libraryUnfiled  = r.unfiled;
        this.libraryShared   = r.shared;
        this.libraryLoading  = false;
      },
      error: () => { this.libraryLoading = false; },
    });
  }

  // ── Save entry ─────────────────────────────────────────────────────────────

  onSaveToLibrary(): void {
    const name = this.newLibraryName.trim();
    if (!name || this.savingLibrary) return;
    const rules = this.sortedRules.map(r => ({ conditionTypeId: r.conditionTypeId, actionTypeId: r.actionTypeId }));
    const desc  = this.newLibraryDescription.trim();
    this.savingLibrary = true;
    this.libraryError = '';
    this.auth.saveAiLibrary(name, desc, rules, this.newLibraryFolderId).subscribe({
      next: entry => {
        if (entry.folderId) {
          const folder = this.libraryFolders.find(f => f.folderId === entry.folderId);
          if (folder) folder.entries.push(entry);
        } else {
          this.libraryUnfiled.push(entry);
        }
        this.newLibraryName        = '';
        this.newLibraryDescription = '';
        this.newLibraryFolderId    = null;
        this.showSaveLibrary       = false;
        this.savingLibrary         = false;
      },
      error: (e: any) => {
        this.libraryError = e?.error?.error || 'Failed to save to library.';
        this.savingLibrary = false;
      },
    });
  }

  // ── Delete entry ───────────────────────────────────────────────────────────

  onDeleteLibraryEntry(entry: AiLibrarySnapshot): void {
    this.libraryError = '';
    this.auth.deleteAiLibrary(entry.libraryId).subscribe({
      next: () => {
        if (entry.folderId) {
          const folder = this.libraryFolders.find(f => f.folderId === entry.folderId);
          if (folder) folder.entries = folder.entries.filter(e => e.libraryId !== entry.libraryId);
        } else {
          this.libraryUnfiled = this.libraryUnfiled.filter(e => e.libraryId !== entry.libraryId);
        }
      },
      error: (e: any) => { this.libraryError = e?.error?.error || 'Failed to delete library entry.'; },
    });
  }

  // ── Preview entry ──────────────────────────────────────────────────────────

  openLibraryPreview(entry: AiLibrarySnapshot | AiSharedLibrarySnapshot): void {
    this.libraryPreviewEntry = entry;
  }

  closeLibraryPreview(): void {
    this.libraryPreviewEntry = null;
  }

  loadLibraryEntryIntoEditor(): void {
    if (!this.libraryPreviewEntry) return;
    this.onPreviewLibraryEntry(this.libraryPreviewEntry);
    this.closeLibraryModal();
  }

  onPreviewLibraryEntry(entry: AiLibrarySnapshot | AiSharedLibrarySnapshot): void {
    this.pendingImport = entry.rules as AiImportRule[];
    this.importError = '';
    this.showImport = true;
    this.appendToEnd = false;
  }

  // ── Move entry between folders ─────────────────────────────────────────────

  toggleMoveEntry(libraryId: string): void {
    this.movingEntryId = this.movingEntryId === libraryId ? null : libraryId;
  }

  onMoveEntry(entry: AiLibrarySnapshot, targetFolderId: string | null): void {
    if (entry.folderId === targetFolderId) { this.movingEntryId = null; return; }
    this.auth.moveAiLibraryEntry(entry.libraryId, targetFolderId).subscribe({
      next: () => {
        // Remove from current location
        if (entry.folderId) {
          const oldFolder = this.libraryFolders.find(f => f.folderId === entry.folderId);
          if (oldFolder) oldFolder.entries = oldFolder.entries.filter(e => e.libraryId !== entry.libraryId);
        } else {
          this.libraryUnfiled = this.libraryUnfiled.filter(e => e.libraryId !== entry.libraryId);
        }
        // Add to new location
        entry.folderId = targetFolderId;
        if (targetFolderId) {
          const newFolder = this.libraryFolders.find(f => f.folderId === targetFolderId);
          if (newFolder) newFolder.entries.push(entry);
        } else {
          this.libraryUnfiled.push(entry);
        }
        this.movingEntryId = null;
      },
      error: (e: any) => { this.libraryError = e?.error?.error || 'Failed to move entry.'; },
    });
  }

  // ── Library search ─────────────────────────────────────────────────────────

  get filteredLibraryFolders(): AiLibraryFolder[] {
    const q = this.librarySearch.trim().toLowerCase();
    if (!q) return this.libraryFolders;
    return this.libraryFolders.filter(f =>
      f.name.toLowerCase().includes(q) ||
      f.entries.some(e => e.name.toLowerCase().includes(q) || e.description.toLowerCase().includes(q))
    );
  }

  get filteredLibraryUnfiled(): AiLibrarySnapshot[] {
    const q = this.librarySearch.trim().toLowerCase();
    if (!q) return this.libraryUnfiled;
    return this.libraryUnfiled.filter(e =>
      e.name.toLowerCase().includes(q) || e.description.toLowerCase().includes(q)
    );
  }

  get filteredLibraryShared(): AiSharedLibrarySnapshot[] {
    const q = this.librarySearch.trim().toLowerCase();
    if (!q) return this.libraryShared;
    return this.libraryShared.filter(e =>
      e.name.toLowerCase().includes(q) ||
      e.description.toLowerCase().includes(q) ||
      (e.ownerName ?? '').toLowerCase().includes(q) ||
      (e.folderName ?? '').toLowerCase().includes(q)
    );
  }

  filteredFolderEntries(folder: AiLibraryFolder): AiLibrarySnapshot[] {
    const q = this.librarySearch.trim().toLowerCase();
    if (!q) return folder.entries;
    return folder.entries.filter(e =>
      e.name.toLowerCase().includes(q) || e.description.toLowerCase().includes(q)
    );
  }

  // ── Entry share panel ──────────────────────────────────────────────────────

  toggleSharePanel(libraryId: string): void {
    if (this.sharingLibraryId === libraryId) {
      this.sharingLibraryId = null;
      this.shareError = '';
    } else {
      this.sharingLibraryId = libraryId;
      this.shareType = 'fleet';
      this._resetEntryPlayerSearch();
      this.shareError = '';
    }
  }

  onSearchSharePlayer(): void {
    const q = this.sharePlayerQuery.trim();
    if (!q) return;
    this.sharePlayerSearching = true;
    this.sharePlayerResults = [];
    this.shareError = '';
    this.auth.searchPssUsers(q).subscribe({
      next: users => { this.sharePlayerResults = users; this.sharePlayerSearching = false; },
      error: (e: any) => { this.shareError = e?.error?.error || 'Search failed.'; this.sharePlayerSearching = false; },
    });
  }

  selectSharePlayer(u: { userId: string; userName: string }): void {
    this.sharePlayerSelected = u;
    this.sharePlayerResults = [];
    this.sharePlayerQuery = '';
  }

  private _resetEntryPlayerSearch(): void {
    this.sharePlayerQuery    = '';
    this.sharePlayerResults  = [];
    this.sharePlayerSelected = null;
    this.sharePlayerSearching = false;
  }

  onAddShare(entry: AiLibrarySnapshot): void {
    if (this.sharingInProgress) return;
    const targetId = this.shareType === 'fleet' ? this.allianceId : (this.sharePlayerSelected?.userId ?? '');
    if (!targetId) {
      this.shareError = this.shareType === 'fleet' ? 'No fleet ID available.' : 'Search and select a player first.';
      return;
    }
    this.sharingInProgress = true;
    this.shareError = '';
    this.auth.shareAiLibrary(entry.libraryId, this.shareType, targetId).subscribe({
      next: share => {
        entry.shares.push(share);
        this.sharingInProgress = false;
        this._resetEntryPlayerSearch();
      },
      error: (e: any) => {
        this.shareError = e?.error?.error || 'Failed to share.';
        this.sharingInProgress = false;
      },
    });
  }

  onRemoveEntryShare(entry: AiLibrarySnapshot, share: AiLibraryShare): void {
    this.shareError = '';
    this.auth.unshareAiLibrary(share.shareId).subscribe({
      next: () => { entry.shares = entry.shares.filter(s => s.shareId !== share.shareId); },
      error: (e: any) => { this.shareError = e?.error?.error || 'Failed to remove share.'; },
    });
  }

  // ── Folder CRUD ────────────────────────────────────────────────────────────

  onSaveFolder(name?: string): void {
    const folderName = (name ?? this.newFolderName).trim();
    if (!folderName || this.savingFolder) return;
    this.savingFolder = true;
    this.libraryError = '';
    this.auth.saveAiFolder(folderName).subscribe({
      next: folder => {
        this.libraryFolders.push(folder);
        this.newFolderName  = '';
        this.showNewFolder  = false;
        this.savingFolder   = false;
      },
      error: (e: any) => {
        this.libraryError = e?.error?.error || 'Failed to create folder.';
        this.savingFolder = false;
      },
    });
  }

  onSaveInlineFolder(): void {
    const name = this.inlineFolderName.trim();
    if (!name || this.savingInlineFolder) return;
    this.savingInlineFolder = true;
    this.inlineFolderError  = '';
    this.auth.saveAiFolder(name).subscribe({
      next: folder => {
        this.libraryFolders.push(folder);
        this.newLibraryFolderId  = folder.folderId; // auto-select the new folder
        this.inlineFolderName    = '';
        this.showInlineFolderModal = false;
        this.savingInlineFolder  = false;
      },
      error: (e: any) => {
        this.inlineFolderError  = e?.error?.error || 'Failed to create folder.';
        this.savingInlineFolder = false;
      },
    });
  }

  onDeleteFolder(folder: AiLibraryFolder): void {
    this.libraryError = '';
    this.auth.deleteAiFolder(folder.folderId).subscribe({
      next: () => {
        this.libraryUnfiled.push(...folder.entries.map(e => ({ ...e, folderId: null })));
        this.libraryFolders = this.libraryFolders.filter(f => f.folderId !== folder.folderId);
      },
      error: (e: any) => { this.libraryError = e?.error?.error || 'Failed to delete folder.'; },
    });
  }

  selectLibraryView(type: 'folder' | 'unfiled' | 'shared', folderId?: string): void {
    const isSame = this.selectedLibraryView?.type === type && this.selectedLibraryView?.folderId === folderId;
    this.selectedLibraryView = isSame ? null : { type, folderId };
  }

  // ── Folder share panel ─────────────────────────────────────────────────────

  toggleFolderSharePanel(folderId: string): void {
    if (this.sharingFolderId === folderId) {
      this.sharingFolderId = null;
      this.folderShareError = '';
    } else {
      this.sharingFolderId = folderId;
      this.folderShareType = 'fleet';
      this._resetFolderPlayerSearch();
      this.folderShareError = '';
    }
  }

  onSearchFolderSharePlayer(): void {
    const q = this.folderSharePlayerQuery.trim();
    if (!q) return;
    this.folderSharePlayerSearching = true;
    this.folderSharePlayerResults = [];
    this.folderShareError = '';
    this.auth.searchPssUsers(q).subscribe({
      next: users => { this.folderSharePlayerResults = users; this.folderSharePlayerSearching = false; },
      error: (e: any) => { this.folderShareError = e?.error?.error || 'Search failed.'; this.folderSharePlayerSearching = false; },
    });
  }

  selectFolderSharePlayer(u: { userId: string; userName: string }): void {
    this.folderSharePlayerSelected = u;
    this.folderSharePlayerResults  = [];
    this.folderSharePlayerQuery    = '';
  }

  private _resetFolderPlayerSearch(): void {
    this.folderSharePlayerQuery    = '';
    this.folderSharePlayerResults  = [];
    this.folderSharePlayerSelected = null;
    this.folderSharePlayerSearching = false;
  }

  onAddFolderShare(folder: AiLibraryFolder): void {
    if (this.folderSharingInProgress) return;
    const targetId = this.folderShareType === 'fleet' ? this.allianceId : (this.folderSharePlayerSelected?.userId ?? '');
    if (!targetId) {
      this.folderShareError = this.folderShareType === 'fleet' ? 'No fleet ID available.' : 'Search and select a player first.';
      return;
    }
    this.folderSharingInProgress = true;
    this.folderShareError = '';
    this.auth.shareAiFolder(folder.folderId, this.folderShareType, targetId).subscribe({
      next: share => {
        folder.shares.push(share);
        this.folderSharingInProgress = false;
        this._resetFolderPlayerSearch();
      },
      error: (e: any) => {
        this.folderShareError = e?.error?.error || 'Failed to share folder.';
        this.folderSharingInProgress = false;
      },
    });
  }

  onRemoveFolderShare(folder: AiLibraryFolder, share: AiLibraryShare): void {
    this.folderShareError = '';
    this.auth.unshareAiFolder(share.shareId).subscribe({
      next: () => { folder.shares = folder.shares.filter(s => s.shareId !== share.shareId); },
      error: (e: any) => { this.folderShareError = e?.error?.error || 'Failed to remove folder share.'; },
    });
  }

  // ── Shared helpers ─────────────────────────────────────────────────────────

  shareLabel(share: AiLibraryShare): string {
    if (share.shareType === 'fleet') return `Fleet (${share.sharedWithAllianceId})`;
    return share.sharedWithName ?? share.sharedWithUserId ?? 'Unknown player';
  }
}
